/**
 * @fileoverview GenerateOptions Component - AI generation options for incident reports
 * Allows users to select annotations and provide additional context for AI report generation.
 * 
 * @author SmarterServices Platform Team
 * @version 1.0.0
 * @since 2025-10-21
 */

import React, { useState, useEffect } from 'react';
import { 
  View, 
  Flex, 
  Button, 
  Heading, 
  Text, 
  TextArea, 
  Checkbox, 
  List,
  Pill,
  IconFlagLine,
  IconAiColoredLine
} from '@smarterservices/smarter-ui';
import { getStore } from '../../../stores/StoreContext';

/**
 * GenerateOptions Component
 * 
 * Provides interface for AI-powered incident report generation:
 * - Displays all flag-type annotations from the session
 * - Allows selection of relevant flags to include
 * - Provides text area for additional context
 * - Generates report using selected data
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onGenerate - Callback when report is generated with {subject, details}
 * @param {Function} props.onCancel - Callback when user cancels generation
 * @returns {JSX.Element} The rendered generate options interface
 */
function GenerateOptions({ onGenerate, onCancel }) {
  const [selectedFlags, setSelectedFlags] = useState(new Set());
  const [additionalContext, setAdditionalContext] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [annotations, setAnnotations] = useState([]);

  const AppStore = getStore('appStore');
  const ExamSessionStore = getStore('examSessionStore');

  // Load annotations on mount
  useEffect(() => {
    // Mock annotations data - replace with actual API call
    const mockAnnotations = [
      {
        id: 'ann_1',
        type: 'flag',
        flagType: 'suspicious_behavior',
        timestamp: '10:23',
        description: 'Student looking away from screen repeatedly',
        severity: 'medium'
      },
      {
        id: 'ann_2', 
        type: 'flag',
        flagType: 'audio_issue',
        timestamp: '15:45',
        description: 'Background noise detected',
        severity: 'low'
      },
      {
        id: 'ann_3',
        type: 'flag', 
        flagType: 'identity_verification',
        timestamp: '02:10',
        description: 'Face not clearly visible',
        severity: 'high'
      },
      {
        id: 'ann_4',
        type: 'flag',
        flagType: 'prohibited_item',
        timestamp: '22:33',
        description: 'Possible phone visible on desk',
        severity: 'high'
      }
    ];
    
    // Filter to only flag-type annotations
    const flagAnnotations = mockAnnotations.filter(ann => ann.type === 'flag');
    setAnnotations(flagAnnotations);
  }, []);

  // Handle flag selection
  const handleFlagToggle = (flagId) => {
    const newSelected = new Set(selectedFlags);
    if (newSelected.has(flagId)) {
      newSelected.delete(flagId);
    } else {
      newSelected.add(flagId);
    }
    setSelectedFlags(newSelected);
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (selectedFlags.size === annotations.length) {
      setSelectedFlags(new Set());
    } else {
      setSelectedFlags(new Set(annotations.map(ann => ann.id)));
    }
  };

  // Mock AI generation
  const handleGenerate = async () => {
    if (selectedFlags.size === 0) {
      alert('Please select at least one flag to include in the report.');
      return;
    }

    setIsGenerating(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Get selected annotation details
      const selectedAnnotations = annotations.filter(ann => selectedFlags.has(ann.id));
      
      // Mock AI response - in real implementation, this would be an API call
      const mockResponse = {
        subject: `Incident Report - Multiple Violations Detected`,
        details: `During the examination session, several concerning behaviors and technical issues were observed:

${selectedAnnotations.map(ann => 
  `• ${ann.timestamp} - ${ann.flagType.replace('_', ' ').toUpperCase()}: ${ann.description} (Severity: ${ann.severity})`
).join('\n')}

${additionalContext ? `\nAdditional Context:\n${additionalContext}` : ''}

These incidents require further review and may indicate potential academic integrity violations or technical issues that affected the examination environment.`
      };

      // Call the onGenerate callback with the generated content
      onGenerate(mockResponse);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  return (
    <View as="div" padding="medium">
      <Heading level="h3" margin="none none medium none">
        <IconAiColoredLine /> Generate Report with AI
      </Heading>
      
      <Text as="p" color="secondary" margin="none none large none">
        Select the flagged annotations you want to include in your incident report. 
        The AI will generate a comprehensive report based on your selections.
      </Text>

      {annotations.length > 0 ? (
        <>
          <Flex justifyItems="space-between" alignItems="center" margin="none none medium none">
            <Heading level="h4" margin="none">
              Flagged Annotations ({annotations.length})
            </Heading>
            <Button
              color="secondary"
              size="small"
              onClick={handleSelectAll}
            >
              {selectedFlags.size === annotations.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Flex>

          <List margin="none none large none">
            {annotations.map((annotation) => (
              <List.Item key={annotation.id}>
                <Flex alignItems="start" gap="small">
                  <Flex.Item>
                    <Checkbox
                      id={`flag-${annotation.id}`}
                      checked={selectedFlags.has(annotation.id)}
                      onChange={() => handleFlagToggle(annotation.id)}
                    />
                  </Flex.Item>
                  <Flex.Item shouldGrow>
                    <View as="div">
                      <Flex alignItems="center" gap="small" margin="none none x-small none">
                        <IconFlagLine size="small" />
                        <Text weight="bold">{annotation.timestamp}</Text>
                        <Pill 
                          color={getSeverityColor(annotation.severity)}
                          size="small"
                        >
                          {annotation.severity}
                        </Pill>
                      </Flex>
                      <Text as="div" size="small" color="secondary" margin="none none x-small none">
                        {annotation.flagType.replace('_', ' ').toUpperCase()}
                      </Text>
                      <Text as="div">
                        {annotation.description}
                      </Text>
                    </View>
                  </Flex.Item>
                </Flex>
              </List.Item>
            ))}
          </List>

          <View as="div" margin="none none large none">
            <TextArea
              renderLabel="Additional Context (Optional)"
              placeholder="Add any additional information or context that should be included in the report..."
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              maxHeight="8rem"
            />
          </View>

          <Flex justifyItems="end" gap="small">
            <Button
              color="secondary"
              onClick={onCancel}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              color="success"
              onClick={handleGenerate}
              disabled={isGenerating || selectedFlags.size === 0}
            >
              {isGenerating ? (
                <>
                  <IconAiColoredLine /> Generating...
                </>
              ) : (
                <>
                  <IconAiColoredLine /> Generate Report
                </>
              )}
            </Button>
          </Flex>
        </>
      ) : (
        <View as="div" textAlign="center" padding="large">
          <Text color="secondary">
            No flagged annotations found in this session. 
            You can still create a report from scratch.
          </Text>
          <Button
            color="primary"
            onClick={onCancel}
            margin="medium none none none"
          >
            Create from Scratch Instead
          </Button>
        </View>
      )}
    </View>
  );
}

export default GenerateOptions;